import { useTranslations } from 'next-intl'
import DateTimeField from '@/kmsApp/components/forms/datetime-picker'
import { Field } from '@/kmsApp/components/forms/field'
import { Select } from '@/kmsApp/components/forms/select'
import Section from '@/kmsApp/components/layout/section'
import Button from '@/kmsApp/components/shared/ui/button'
import { FieldErrors, useWatch } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'
import { AdresseFavorite } from '@/kmsApp/types/models/adresse-favorite'
import Icon from '@/kmsApp/components/shared/ui/icon'
import { cn } from '@/kmsApp/utils/cn'
import { CourseDatesFields } from './CourseDatesFields'
import { REQUIRED } from '@/kmsApp/constants/regex-const'

// Sous-composant pour isoler les Hooks par arrêt
export const StopItem = ({
  field,
  index,
  control,
  register,
  errors,
  fieldsCount,
  addEscale,
  removeEscale,
  openFavoriteDrawer,
  openAddressSearch,
  getLabelAndIconFromOrdre,
  isEscale,
  dateCourse,
  trajetretour,
  dateTrajetRetour,
  favorites = [],
}: {
  field: any
  index: number
  control: any
  register: any
  errors: FieldErrors<CourseFormType>
  fieldsCount: number
  addEscale: () => void
  removeEscale: (indexToRemove: number) => void
  openFavoriteDrawer: ((index: number) => void) | null | undefined
  openAddressSearch: (index: number) => void
  getLabelAndIconFromOrdre: (ordre: number) => {
    labelOrdre: string
    iconOrdre: string
  }
  isEscale: (ordre: number) => boolean
  dateCourse: string
  trajetretour: boolean
  dateTrajetRetour: string | null | undefined
  favorites?: AdresseFavorite[]
}) => {
  const t = useTranslations('web_course_form')
  const tCommon = useTranslations('common')
  // Les hooks useWatch sont maintenant isolés dans ce composant et s'exécutent de façon stable !
  const stopType = useWatch({ control, name: `arrets.${index}.type` })
  const currentAdresse = useWatch({ control, name: `arrets.${index}.adresse` })
  const avecTrajetRetour = useWatch({
    control,
    name: `avecTrajetRetour`,
  })

  const isFirstStop = index === 0
  const isLastStop = index === fieldsCount - 1
  const isTransportHub = stopType === 1 || stopType === 2
  const nomLieu = currentAdresse?.nom?.trim() || null
  // Une adresse est considérée comme renseignée dès qu'on a une rue, une
  // ville, ou un nom de lieu (POI/Favori sans rue exploitable).
  const hasFilledAddress = !!(
    currentAdresse &&
    (currentAdresse.rue || currentAdresse.ville || nomLieu)
  )

  const isFavorite =
    hasFilledAddress &&
    favorites.some(
      (fav) =>
        fav.adresse.rue.trim().toLowerCase() === currentAdresse?.rue?.trim().toLowerCase() &&
        fav.adresse.codePostal === currentAdresse?.codePostal &&
        fav.adresse.ville.trim().toLowerCase() === currentAdresse?.ville?.trim().toLowerCase(),
    )

  const { labelOrdre, iconOrdre } = getLabelAndIconFromOrdre(field.ordre)
  const addressSummary = hasFilledAddress
    ? [
        `${[currentAdresse.rue, currentAdresse.numero].filter(Boolean).join(' ')}${currentAdresse.boite ? ` / ${currentAdresse.boite}` : ''}`,
        [currentAdresse.codePostal, currentAdresse.ville].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : null

  return (
    <Section titre={labelOrdre} icon={iconOrdre} className="my-4">
      {!isLastStop && (
        <Button
          type="button"
          variant="secondary"
          icon="add_location"
          onClick={addEscale}
          className="w-full sm:w-auto mb-5"
        >
          {t('add_stop')}
        </Button>
      )}
      {openFavoriteDrawer && (
        <button
          type="button"
          onClick={() => openFavoriteDrawer(index)}
          title={t('save_as_favorite')}
          className={`p-2 rounded-full transition-all duration-200 ${
            isFavorite
              ? 'text-amber-400'
              : 'text-slate-300 dark:text-slate-600 hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20'
          }`}
        >
          <div className="flex items-center">
            <Icon
              name="star"
              filled={isFavorite}
              className={cn('text-xl', isFavorite && 'text-amber-400')}
            />
            <span className="ml-1">
              {isFavorite ? t('saved') : t('save_address_to_favorites')}
            </span>
          </div>
        </button>
      )}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-6 items-start">
        <div className="w-full sm:w-1/2">
          <label className="text-sm font-semibold text-slate-600 dark:text-white">{t('address')}</label>
          <button
            type="button"
            onClick={() => openAddressSearch(index)}
            className={cn(
              'flex-1 min-w-0 w-full flex items-center gap-3 px-4 py-3 rounded-xl border',
              'border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30',
              'hover:border-primary/40 hover:bg-primary/5 dark:hover:bg-primary/5 transition-all group/search',
              errors.arrets?.[index] && !hasFilledAddress && 'ring-2 ring-red-500',
            )}
          >
            <Icon
              name="search"
              className="text-lg text-slate-400 group-hover/search:text-primary transition-colors"
            />
            {hasFilledAddress ? (
              <span className="flex-1 min-w-0 flex flex-col text-left">
                {nomLieu ? (
                  <>
                    <span className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                      {nomLieu}
                    </span>
                    {addressSummary && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {addressSummary}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-sm font-medium text-slate-800 dark:text-white truncate">
                    {addressSummary}
                  </span>
                )}
              </span>
            ) : (
              <span className="flex-1 text-left text-sm text-slate-400 dark:text-slate-500">
                {t('search_address_placeholder')}
              </span>
            )}
            <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-[10px] font-medium text-slate-400 bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
              {t('search_hint')}
            </kbd>
          </button>
        </div>
        {/* Boîte toujours modifiable et à côté de l'adresse : les
            suggestions (Google/BAN) n'incluent jamais le numéro de boîte,
            même quand le numéro de rue est détecté. */}
        {hasFilledAddress && (
          <div className="w-full sm:w-1/2">
            <Field titre={t('box')} {...register(`arrets.${index}.adresse.boite`)} />
          </div>
        )}
      </div>

      {errors.arrets?.[index] && !hasFilledAddress && (
        <p className={'text-red-500 mt-2'}>{errors.arrets?.[index].adresse?.rue?.message}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 pt-1">
        <Field
          titre={t('street')}
          placeholder={t('street_placeholder')}
          error={errors.arrets?.[index]?.adresse?.rue?.message}
          {...register(`arrets.${index}.adresse.rue`, {
            // Une adresse est valide dès qu'un de ses champs est renseigné :
            // certaines suggestions (POI, favoris) n'ont pas de rue, parfois
            // pas de code postal, etc. — seul un nom de lieu ou une ville
            // peut suffire à les identifier.
            validate: (value: any, formValues: any) => {
              const adresse = formValues.arrets?.[index]?.adresse
              return (
                !!(
                  value?.trim() ||
                  adresse?.ville?.trim() ||
                  adresse?.codePostal?.trim() ||
                  adresse?.nom?.trim()
                ) || t('select_address')
              )
            },
          })}
          containerClassName="hidden"
        />
        {/* Numéro : affiché uniquement quand l'adresse est sélectionnée mais
            que la suggestion ne l'a pas renseigné, pour permettre une saisie
            manuelle. */}
        {hasFilledAddress && !currentAdresse?.isNumDetected && (
          <Field
            titre={t('number')}
            error={errors.arrets?.[index]?.adresse?.numero?.message}
            {...register(`arrets.${index}.adresse.numero`, {
              required: REQUIRED(tCommon).required,
            })}
            containerClassName="col-span-1 lg:col-span-2"
          />
        )}

        <div className="sm:col-span-2 lg:col-span-4">
          <Select
            control={control}
            titre={t('stop_type')}
            placeholder={t('select_option')}
            error={errors.arrets?.[index]?.type?.message}
            options={[
              { value: 0, label: t('option_address') },
              { value: 1, label: t('option_plane') },
              { value: 2, label: t('option_train') },
            ]}
            {...register(`arrets.${index}.type`, {
              required: REQUIRED(tCommon).required,
              valueAsNumber: true,
            })}
          />
        </div>

        {isTransportHub && (
          <div className="sm:col-span-2 lg:col-span-4 border-2 border-dashed rounded-lg p-3 md:p-4 border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 mb-3">
              <Icon
                name={stopType === 1 ? 'flight' : 'train'}
                className="text-base text-blue-500"
              />
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                {stopType === 1 ? t('flight_info') : t('train_info')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <Field
                titre={t('company')}
                {...register(`arrets.${index}.companyTransport`)}
              />
              <Field
                titre={stopType === 1 ? t('flight_number') : t('train_number')}
                {...register(`arrets.${index}.numeroTransport`)}
              />
            </div>
          </div>
        )}

        <Field
          titre={t('postal_code')}
          placeholder={t('postal_code_placeholder')}
          error={errors.arrets?.[index]?.adresse?.codePostal?.message}
          {...register(`arrets.${index}.adresse.codePostal`)}
          containerClassName="hidden"
        />
        <Field
          titre={t('city')}
          placeholder={t('city_placeholder')}
          error={errors.arrets?.[index]?.adresse?.ville?.message}
          {...register(`arrets.${index}.adresse.ville`, {
            required: REQUIRED(tCommon).required,
          })}
          containerClassName="hidden"
        />
        <Field
          titre={t('country')}
          placeholder={t('country_placeholder')}
          error={errors.arrets?.[index]?.adresse?.pays?.message}
          {...register(`arrets.${index}.adresse.pays`, {
            required: REQUIRED(tCommon).required,
          })}
          containerClassName="hidden"
        />
      </div>
      <CourseDatesFields
        control={control}
        register={register}
        errors={errors}
        showDepart={isFirstStop}
        showRetour={isLastStop}
      />
      {isEscale(field.ordre) && (
        <div className="flex justify-end mt-2">
          <Button type="button" variant="secondary" onClick={() => removeEscale(index)}>
            {t('remove_stop')}
          </Button>
        </div>
      )}
    </Section>
  )
}
